import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import MathNodeView from './MathNodeView';

export interface MathInlineOptions {
  /** Opens the host editor's modal to edit an existing formula; receives the apply callback. */
  onEdit?: (latex: string, apply: (latex: string) => void) => void;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mathInline: {
      /** Insert a math/chemistry formula node from a LaTeX string. */
      insertMath: (latex: string, display?: boolean) => ReturnType;
    };
  }
}

/**
 * An atomic inline node holding a LaTeX formula. Persisted as
 * `<span data-math="LATEX" data-display="true|false">` so the read-only viewer can
 * re-render it with KaTeX without storing bulky markup.
 */
export const MathInline = Node.create<MathInlineOptions>({
  name: 'mathInline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { onEdit: undefined };
  },

  addAttributes() {
    return {
      latex: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-math') || '',
        renderHTML: (attrs) => ({ 'data-math': attrs.latex }),
      },
      display: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-display') === 'true',
        renderHTML: (attrs) => ({ 'data-display': attrs.display ? 'true' : 'false' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-math]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MathNodeView);
  },

  addCommands() {
    return {
      insertMath:
        (latex: string, display = false) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { latex, display } }),
    };
  },
});
